import type { ThoughtStep } from '@/utils/aiThought';
import type { ProjectWbsSnapshot } from '@/api/projectApi';

export type WbsGenerateStepKey = 'collect' | 'arrange' | 'render';

export type WbsGenerateStepStatus = 'pending' | 'processing' | 'done' | 'failed';

export type WbsGenerateProgressStep = {
  key: WbsGenerateStepKey | string;
  title?: string;
  status: WbsGenerateStepStatus | string;
  text?: string;
};

const STEP_META: Record<WbsGenerateStepKey, { title: string; pendingText: string; processingText: string }> = {
  collect: {
    title: '梳理项目信息',
    pendingText: '等待梳理项目信息…',
    processingText: '正在梳理项目信息…',
  },
  arrange: {
    title: '编排任务',
    pendingText: '等待编排任务…',
    processingText: '正在编排任务…',
  },
  render: {
    title: '渲染 WBS',
    pendingText: '等待渲染…',
    processingText: '正在渲染…',
  },
};

const STEP_ORDER: WbsGenerateStepKey[] = ['collect', 'arrange', 'render'];

export const WBS_GENERATE_LIVE_STEPS: ThoughtStep[] = STEP_ORDER.map((key) => ({
  key,
  icon: '⏳',
  title: STEP_META[key].title,
  text: STEP_META[key].processingText,
}));

/** 初始：仅展示「梳理项目信息」进行中 */
export function wbsGenerateThoughtInitial(): ThoughtStep[] {
  return [
    {
      key: 'collect',
      icon: '⏳',
      title: STEP_META.collect.title,
      text: STEP_META.collect.processingText,
    },
  ];
}

export function wbsGenerateThoughtDone(): ThoughtStep[] {
  return STEP_ORDER.map((key) => ({
    key,
    icon: '✅',
    title: STEP_META[key].title,
    text: key === 'collect'
      ? '项目信息已整理完成'
      : key === 'arrange'
        ? '三方任务编排已完成'
        : 'WBS 已渲染并同步到任务计划',
  }));
}

/** 将后端轮询 steps 转为 AI 对话框思考步骤 */
export function mapWbsGenerateProgressToThought(steps?: WbsGenerateProgressStep[] | null): ThoughtStep[] {
  if (!steps?.length) {
    return wbsGenerateThoughtInitial();
  }
  const byKey = new Map<string, WbsGenerateProgressStep>();
  for (const s of steps) {
    if (s?.key) byKey.set(String(s.key), s);
  }
  const out: ThoughtStep[] = [];
  for (const key of STEP_ORDER) {
    const raw = byKey.get(key);
    const status = normalizeStatus(raw?.status);
    // 尚未开始的后续步骤不展示，避免与真实执行脱节
    if (status === 'pending') break;
    const meta = STEP_META[key];
    const text = (raw?.text || '').trim()
      || (status === 'done'
        ? (key === 'collect'
          ? '项目信息已整理完成'
          : key === 'arrange'
            ? '三方任务编排已完成'
            : 'WBS 已渲染并同步到任务计划')
        : meta.processingText);
    out.push({
      key,
      icon: status === 'done' ? '✅' : status === 'failed' ? '❌' : '⏳',
      title: (raw?.title || meta.title).trim() || meta.title,
      text,
    });
    if (status === 'failed') break;
  }
  return out.length ? out : wbsGenerateThoughtInitial();
}

function normalizeStatus(status?: string): WbsGenerateStepStatus {
  const s = (status || '').trim().toLowerCase();
  if (s === 'done' || s === 'completed' || s === 'success') return 'done';
  if (s === 'failed' || s === 'error') return 'failed';
  if (s === 'processing' || s === 'running' || s === 'in_progress') return 'processing';
  return 'pending';
}

/** 根据三方返回快照拼一段对话总结（不拼接三方原始 message，避免乱码） */
export function buildWbsGenerateSummary(
  projectName: string,
  snapshot: ProjectWbsSnapshot | null | undefined,
  syncedTaskCount?: number | null,
): string {
  const name = (projectName || '').trim() || '当前项目';
  const content = snapshot?.content;
  const root = content && typeof content === 'object' ? (content as Record<string, unknown>) : null;
  const merge =
    root?.merge_summary && typeof root.merge_summary === 'object'
      ? (root.merge_summary as Record<string, unknown>)
      : null;

  const finalCount = toNonNegInt(merge?.final_task_count)
    ?? toNonNegInt(Array.isArray(root?.tasks) ? root!.tasks.length : null)
    ?? toNonNegInt(Array.isArray(root?.wbs_items) ? root!.wbs_items.length : null);
  const assigned = toNonNegInt(merge?.assigned_count);
  const unassigned = toNonNegInt(merge?.unassigned_count);
  const phaseCount = Array.isArray(root?.phases) ? root!.phases.length : 0;
  const synced = toNonNegInt(syncedTaskCount);

  const parts: string[] = [
    `已为「${name}」完成 WBS 生成。`,
  ];
  if (finalCount != null) {
    let line = `本次共生成 ${finalCount} 条任务`;
    if (phaseCount > 0) {
      line += `，覆盖 ${phaseCount} 个项目阶段`;
    }
    parts.push(`${line}。`);
  } else if (phaseCount > 0) {
    parts.push(`已按 ${phaseCount} 个固定阶段编排任务。`);
  }
  if (assigned != null || unassigned != null) {
    const a = assigned ?? 0;
    const u = unassigned ?? 0;
    parts.push(`其中已分配负责人 ${a} 条，待分配 ${u} 条。`);
  }
  if (synced != null && synced > 0) {
    parts.push(`已同步 ${synced} 条任务到侧栏任务计划，可直接查看与确认。`);
  } else {
    parts.push('结果已同步到 WBS 快照与任务计划，可在任务侧栏查看与确认。');
  }
  return parts.join('');
}

function toNonNegInt(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return Math.floor(v);
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) {
    const n = Math.floor(Number(v));
    return n >= 0 ? n : null;
  }
  return null;
}
